/**
 * Auth Store
 * @typedef {Object} AuthStore
 * @property {boolean} authenticated - Indicates if the user is authenticated.
 * @property {Object} user - User object containing user information.
 * @property {boolean} loadingProfile - Indicates if the user profile is being loaded.
 * @property {string} token - User token.
 * @property {string} refreshToken - Refresh token.
 * @property {number|null} tokenExpires - Expiration time of the user token.
 * @property {number|null} refreshTokenExpires - Expiration time of the refresh token.
 */

/**
 * Auth Store
 * @type {import('pinia').Store<AuthStore>}
 */
import { defineStore } from 'pinia';
import keycloakService from '@services/keycloakService';
import { jwtDecode } from 'jwt-decode';
import { getUserProfile } from '@/services/contactLambda';
import SecureLS from 'secure-ls';
import { BASE_STORAGE_KEY, ORGANIZATION_TYPES } from '@/utils/constants';
import logger from '@/utils/logger';

// Create an instance of SecureLS with encryption enabled
const ls = new SecureLS({ encodingType: 'aes' });

export const useAuthStore = defineStore('authStore',{
    state: () => {
        return {
            authenticated: false,
            user: null,
            loadingProfile: false,
            profileLoaded: false,
            token: '',
            refreshToken: '',
            tokenExpires: null,
            refreshTokenExpires: null,
            primaryOrganizationChanged: false,
        };
    },
    persist: {
        storage: {
            getItem: (key) => {
                const data = ls.get(key);
                return data ? JSON.parse(data) : null;
            },
            setItem: (key, value) => {
                ls.set(key, JSON.stringify(value));
            },
            removeItem: (key) => {
                ls.remove(key);
            },
        },
    },
    getters: {},
    actions: {
        async initOauth(keycloak) {
            if (this.loadingProfile) {
                return;
            }

            if (keycloak.authenticated) {
                this.loadingProfile = true;
                this.authenticated = keycloak.authenticated;
                if (keycloak.tokenParsed && keycloak.tokenParsed.exp) {
                    this.tokenExpires = keycloak.tokenParsed.exp;
                }

                const decodedToken = jwtDecode(keycloak.refreshToken);
                if (decodedToken && decodedToken.exp) {
                    this.refreshTokenExpires = decodedToken.exp;
                }

                this.token = keycloak.token;
                this.refreshToken = keycloak.refreshToken;

                if (this.user == null || !this.profileLoaded) {
                    try {
                        const userProfile = await keycloakService.GetUserProfile();
                        this.user = userProfile;
                        const dbProfile = await getUserProfile();
                        const orgTypes = dbProfile.primaryOrganizationTypes ? dbProfile.primaryOrganizationTypes.split(',') : [];
                        this.user.firstName = dbProfile.firstName;
                        this.user.lastName = dbProfile.lastName;
                        this.user.primaryOrganizationId = dbProfile.primaryOrganizationId;
                        this.user.primaryOrganizationName = dbProfile.primaryOrganizationName;
                        this.user.primaryOrganizationTypes = orgTypes;
                        this.user.isTpaUser = orgTypes.includes(ORGANIZATION_TYPES.TPA);
                        this.user.isHceOnlyUser = orgTypes.includes(ORGANIZATION_TYPES.HCE) && orgTypes.length === 1;
                        this.primaryOrganizationChanged = false;
                        this.profileLoaded = true;
                    } catch (error) {
                        logger.error(error);
                        keycloakService.CallLogout();
                        this.clearUserData();
                    } finally {
                        this.loadingProfile = false;
                        this.clearHepLocalStorage();
                    }
                } else {
                    this.profileLoaded = true;
                    this.loadingProfile = false;
                }
            } else {
                this.clearUserData();
                this.loadingProfile = false;
                this.profileLoaded = true;
            }
        },
        async login(redirectUri) {
            try {
                await keycloakService.CallLogin(redirectUri);
                this.clearUserData();
                this.initOauth(keycloakService.keycloak);
            } catch (error) {
                logger.error(error);
            }
        },
        async logout() {
            try {
                this.clearUserData();
                await keycloakService.CallLogout();
            } catch (error) {
                logger.error(error);
            }
        },
        async refreshUserToken() {
            try {
                const keycloak = await keycloakService.CallTokenRefresh();
                await this.initOauth(keycloak);

            } catch (error) {
                logger.error(error);
            }
        },
        async checkTokenExpiration() {
            if (this.isTokenExpired()) {
                await this.refreshUserToken();
            }
        },
        isTokenExpired() {
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds since the epoch
            return currentTime >= this.tokenExpires;
        },
        isRefreshTokenExpired() {
            const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds since the epoch
            return currentTime >= this.refreshTokenExpires;
        },
        async refreshUserProfile() {
            var dbProfile = await getUserProfile();
            const orgTypes = dbProfile.primaryOrganizationTypes ? dbProfile.primaryOrganizationTypes.split(',') : [];
            this.user.primaryOrganizationId = dbProfile.primaryOrganizationId;
            this.user.primaryOrganizationName = dbProfile.primaryOrganizationName;
            this.user.primaryOrganizationTypes = orgTypes;
            this.primaryOrganizationChanged = true;
        },
        clearUserData() {
            this.authenticated = false;
            this.user = null;
            this.profileLoaded = false;
            this.token = '';
            this.refreshToken = '';
            this.clearHepLocalStorage();
        },
        clearHepLocalStorage() {
            // remove keys stored via SecureLS and any raw localStorage keys starting with 'hep'
            try {
            Object.keys(localStorage).forEach((k) => {
                if (!k) return;
                if (k.startsWith(BASE_STORAGE_KEY)) {
                try { ls.remove(k); } catch (e) { /* ignore */ }
                try { localStorage.removeItem(k); } catch (e) { /* ignore */ }
                }
            });
            } catch (err) {
            // fallback: iterate by index if Object.keys failed
            for (let i = localStorage.length - 1; i >= 0; i--) {
                const key = localStorage.key(i);
                if (key && key.startsWith(BASE_STORAGE_KEY)) {
                try { ls.remove(key); } catch (e) { /* empty */ }
                try { localStorage.removeItem(key); } catch (e) { /* empty */}
                }
            }
            }
        },
    },
});
